import React, { useRef, useState, useImperativeHandle, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import MediaPickerModal from "./MediaPickerModal";
import { apiUrl } from "../utils/apis";
import { bucketUrl } from "../utils/helper";
import { Modal, Button, Form } from "react-bootstrap";
import "./TinyMceEditor.css";

// Tiptap Imports
import { DOMParser as ProseMirrorDOMParser } from "prosemirror-model";
import { useEditor, EditorContent, Node, Extension, ReactNodeViewRenderer, NodeViewWrapper } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import TextAlign from "@tiptap/extension-text-align";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import FontFamily from "@tiptap/extension-font-family";
import {
  Undo2, Redo2, Bold, Italic, Underline as UnderlineIcon,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Table as TableIcon, Image as ImageIcon, MapPin, Link2,
  Trash2, Copy, Settings, Indent as IndentIcon, Outdent as OutdentIcon
} from "lucide-react";

const ResizableImage = ({ node, updateAttributes, selected }) => {
  const { src, alt, width, height, alignment, caption } = node.attrs;
  const imgRef = React.useRef(null);
  const [resizing, setResizing] = React.useState(false);
  const [isDraggable, setIsDraggable] = React.useState(true);
  const [aspectRatio, setAspectRatio] = React.useState(1.5); // Default landscape ratio
  const [size, setSize] = React.useState({ width: width || "auto", height: height || "auto" });
  const [captionText, setCaptionText] = React.useState(caption || "");

  React.useEffect(() => {
    setCaptionText(caption || "");
  }, [caption]);

  const onImageLoad = (e) => {
    if (e.target.naturalWidth && e.target.naturalHeight) {
      setAspectRatio(e.target.naturalWidth / e.target.naturalHeight);
    }
  };

  const handlePointerDown = (event, direction) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggable(false);

    const cursorType =
      direction === "top-center" || direction === "bottom-center" ? "ns-resize" :
        direction === "left-center" || direction === "right-center" ? "ew-resize" :
          direction === "top-left" || direction === "bottom-right" || direction === "left" || direction === "right" ? "nwse-resize" :
            "nesw-resize";

    const bodyClass =
      cursorType === "ns-resize" ? "resizing-ns" :
        cursorType === "ew-resize" ? "resizing-ew" :
          cursorType === "nwse-resize" ? "resizing-nwse" :
            "resizing-nesw";

    document.body.classList.add(bodyClass);
    document.body.style.cursor = cursorType;

    const startX = event.clientX;
    const startY = event.clientY;
    const startWidth = imgRef.current ? imgRef.current.clientWidth : 350;
    const startHeight = imgRef.current ? imgRef.current.clientHeight : 250;

    const onPointerMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      let newWidth = startWidth;
      let newHeight = startHeight;

      if (direction === "right-center") {
        newWidth = startWidth + deltaX;
        if (newWidth < 50) newWidth = 50;
      } else if (direction === "left-center") {
        newWidth = startWidth - deltaX;
        if (newWidth < 50) newWidth = 50;
      } else if (direction === "bottom-center") {
        newHeight = startHeight + deltaY;
        if (newHeight < 50) newHeight = 50;
      } else if (direction === "top-center") {
        newHeight = startHeight - deltaY;
        if (newHeight < 50) newHeight = 50;
      } else {
        // Corners - aspect ratio locked
        if (direction === "right" || direction === "bottom-right") {
          newWidth = startWidth + deltaX;
        } else {
          newWidth = startWidth - deltaX;
        }
        if (newWidth < 50) newWidth = 50;
        newHeight = Math.round(newWidth / aspectRatio);
      }

      const finalWidth = `${newWidth}px`;
      const finalHeight = `${newHeight}px`;

      setSize({ width: finalWidth, height: finalHeight });
      updateAttributes({
        width: finalWidth,
        height: finalHeight,
      });
    };

    const onPointerUp = (upEvent) => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.body.classList.remove("resizing-ns", "resizing-ew", "resizing-nwse", "resizing-nesw");
      document.body.style.cursor = "";
      setResizing(false);
      setIsDraggable(true);
    };

    setResizing(true);
    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
  };

  React.useEffect(() => {
    setSize({ width: width || "auto", height: height || "auto" });
  }, [width, height]);

  React.useEffect(() => {
    if (imgRef.current) {
      if (imgRef.current.complete && imgRef.current.naturalWidth && imgRef.current.naturalHeight) {
        setAspectRatio(imgRef.current.naturalWidth / imgRef.current.naturalHeight);
      }
    }
  }, [src]);

  const isIcon = React.useMemo(() => {
    const wVal = parseFloat(width);
    const hVal = parseFloat(height);
    if (!isNaN(wVal) && wVal > 0 && wVal <= 48) return true;
    if (!isNaN(hVal) && hVal > 0 && hVal <= 48) return true;
    if (src && (
      src.includes("icon") ||
      src.includes("logo") ||
      src.includes("star") ||
      src.includes("clock") ||
      src.includes("rating") ||
      src.includes("time") ||
      src.includes("calendar") ||
      src.includes("map-pin") ||
      src.startsWith("data:image/svg")
    )) {
      return true;
    }
    return false;
  }, [width, height, src]);

  let wrapperStyle = {
    display: "inline-block",
    position: "relative",
    userSelect: "all",
    width: size.width,
    height: "auto", // Height is auto to fit caption below image
    maxWidth: "100%", // Prevent overflowing parent containers (like grid columns)
  };

  if (alignment === "left") {
    wrapperStyle.float = "left";
    wrapperStyle.margin = "4px 20px 16px 0";
    wrapperStyle.maxWidth = "50%";
  } else if (alignment === "right") {
    wrapperStyle.float = "right";
    wrapperStyle.margin = "4px 0 16px 20px";
    wrapperStyle.maxWidth = "50%";
  } else if (alignment === "center") {
    wrapperStyle.display = "block";
    wrapperStyle.margin = "16px auto";
    wrapperStyle.textAlign = "center";
    wrapperStyle.clear = "both";
  } else if (alignment === "inline") {
    wrapperStyle.display = "inline-block";
    wrapperStyle.verticalAlign = isIcon ? "middle" : "bottom";
    wrapperStyle.margin = isIcon ? "0 4px" : "8px";
  }

  const imgStyle = {
    width: size.width,
    height: size.height,
    maxWidth: "100%",
    objectFit: isIcon ? "fill" : "cover", // Prevent image stretching/distortion in editor
    display: isIcon ? "inline-block" : "block",
    verticalAlign: isIcon ? "middle" : "bottom",
    transition: resizing ? "none" : "outline 0.15s ease, box-shadow 0.15s ease",
    outline: selected ? "2px solid #0d6efd" : "none",
    boxShadow: selected ? "0 2px 8px rgba(13, 110, 253, 0.2)" : "none",
    borderRadius: isIcon ? "0" : "4px",
  };

  return (
    <NodeViewWrapper
      as="span"
      className={`tiptap-image-wrapper alignment-${alignment} ${isIcon ? "is-icon" : ""}`}
      style={wrapperStyle}
      draggable={isDraggable ? "true" : "false"}
      data-drag-handle
    >
      <div style={{ position: "relative", display: "inline-block", verticalAlign: isIcon ? "middle" : "bottom" }}>
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          onLoad={onImageLoad}
          style={imgStyle}
        />
        {selected && !isIcon && (
          <>
            {/* Corners */}
            <div className="resize-handle top-left" ref={(el) => { if (el) el.onpointerdown = (e) => { e.stopPropagation(); e.preventDefault(); handlePointerDown(e, "left"); }; }} onDragStart={(e) => e.preventDefault()} />
            <div className="resize-handle top-right" ref={(el) => { if (el) el.onpointerdown = (e) => { e.stopPropagation(); e.preventDefault(); handlePointerDown(e, "right"); }; }} onDragStart={(e) => e.preventDefault()} />
            <div className="resize-handle bottom-left" ref={(el) => { if (el) el.onpointerdown = (e) => { e.stopPropagation(); e.preventDefault(); handlePointerDown(e, "bottom-left"); }; }} onDragStart={(e) => e.preventDefault()} />
            <div className="resize-handle bottom-right" ref={(el) => { if (el) el.onpointerdown = (e) => { e.stopPropagation(); e.preventDefault(); handlePointerDown(e, "bottom-right"); }; }} onDragStart={(e) => e.preventDefault()} />

            {/* Sides */}
            <div className="resize-handle top-center" ref={(el) => { if (el) el.onpointerdown = (e) => { e.stopPropagation(); e.preventDefault(); handlePointerDown(e, "top-center"); }; }} onDragStart={(e) => e.preventDefault()} />
            <div className="resize-handle bottom-center" ref={(el) => { if (el) el.onpointerdown = (e) => { e.stopPropagation(); e.preventDefault(); handlePointerDown(e, "bottom-center"); }; }} onDragStart={(e) => e.preventDefault()} />
            <div className="resize-handle left-center" ref={(el) => { if (el) el.onpointerdown = (e) => { e.stopPropagation(); e.preventDefault(); handlePointerDown(e, "left-center"); }; }} onDragStart={(e) => e.preventDefault()} />
            <div className="resize-handle right-center" ref={(el) => { if (el) el.onpointerdown = (e) => { e.stopPropagation(); e.preventDefault(); handlePointerDown(e, "right-center"); }; }} onDragStart={(e) => e.preventDefault()} />
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
};

// Custom FontSize Tiptap Extension

// Custom FontSize Tiptap Extension
const FontSize = Extension.create({
  name: "fontSize",
  addOptions() {
    return {
      types: ["textStyle"],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize?.replace(/['"]+/g, ""),
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize: (fontSize) => ({ chain }) => {
        return chain().setMark("textStyle", { fontSize }).run();
      },
      unsetFontSize: () => ({ chain }) => {
        return chain().setMark("textStyle", { fontSize: null }).removeEmptyTextStyle().run();
      },
    };
  },
});

// Custom LineHeight Tiptap Extension
const LineHeight = Extension.create({
  name: "lineHeight",
  addOptions() {
    return {
      types: ["paragraph", "heading"],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          lineHeight: {
            default: null,
            parseHTML: (element) => element.style.lineHeight,
            renderHTML: (attributes) => {
              if (!attributes.lineHeight) return {};
              return { style: `line-height: ${attributes.lineHeight}` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setLineHeight: (lineHeight) => ({ commands }) => {
        return this.options.types.every((type) =>
          commands.updateAttributes(type, { lineHeight })
        );
      },
      unsetLineHeight: () => ({ commands }) => {
        return this.options.types.every((type) =>
          commands.updateAttributes(type, { lineHeight: null })
        );
      },
    };
  },
});

// Custom Indent Tiptap Extension
const Indent = Extension.create({
  name: "indent",
  addOptions() {
    return {
      types: ["paragraph", "heading"],
      indentSize: 24, // 24px per indent level
      maxIndent: 10,
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: null,
            parseHTML: (element) => {
              const marginLeft = element.style.marginLeft;
              if (!marginLeft) return null;
              const marginVal = parseInt(marginLeft, 10);
              return isNaN(marginVal) ? null : Math.round(marginVal / this.options.indentSize);
            },
            renderHTML: (attributes) => {
              if (!attributes.indent) return {};
              return { style: `margin-left: ${attributes.indent * this.options.indentSize}px` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      indent: () => ({ tr, state }) => {
        const { selection } = state;
        const { from, to } = selection;
        let changed = false;

        state.doc.nodesBetween(from, to, (node, pos) => {
          if (this.options.types.includes(node.type.name)) {
            const currentIndent = node.attrs.indent || 0;
            if (currentIndent < this.options.maxIndent) {
              tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                indent: currentIndent + 1,
              });
              changed = true;
            }
            return false;
          }
        });

        return changed;
      },
      outdent: () => ({ tr, state }) => {
        const { selection } = state;
        const { from, to } = selection;
        let changed = false;

        state.doc.nodesBetween(from, to, (node, pos) => {
          if (this.options.types.includes(node.type.name)) {
            const currentIndent = node.attrs.indent || 0;
            if (currentIndent > 0) {
              const nextIndent = currentIndent - 1;
              tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                indent: nextIndent === 0 ? null : nextIndent,
              });
              changed = true;
            }
            return false;
          }
        });

        return changed;
      },
    };
  },
});

const CustomDiv = Node.create({
  name: "div",
  group: "block",
  content: "block*",
  defining: true,
  parseHTML() {
    return [{ tag: "div", priority: 1000 }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", HTMLAttributes, 0];
  },
  addAttributes() {
    return {
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style"),
        renderHTML: (attributes) => {
          if (!attributes.style) return {};
          return { style: attributes.style };
        },
      },
      className: {
        default: null,
        parseHTML: (element) => element.getAttribute("class"),
        renderHTML: (attributes) => {
          if (!attributes.className) return {};
          return { class: attributes.className };
        },
      },
    };
  },
});

const CustomTable = Table.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      class: {
        default: null,
        parseHTML: (element) => element.getAttribute("class"),
        renderHTML: (attributes) => {
          if (!attributes.class) return {};
          return { class: attributes.class };
        },
      },
      border: {
        default: null,
        parseHTML: (element) => element.getAttribute("border"),
        renderHTML: (attributes) => {
          if (!attributes.border) return {};
          return { border: attributes.border };
        },
      },
    };
  },
});

const CustomImage = Image.extend({
  inline: true,
  group: "inline",
  draggable: true,

  parseHTML() {
    return [
      {
        tag: "figure",
        contentElement: "img",
        parse: (element) => {
          const img = element.querySelector("img");
          const figcaption = element.querySelector("figcaption");
          if (!img) return null;
          return {
            src: img.getAttribute("src"),
            alt: img.getAttribute("alt"),
            title: img.getAttribute("title"),
            width: img.getAttribute("width") || img.style.width,
            height: img.getAttribute("height") || img.style.height,
            alignment: img.getAttribute("data-align") || element.getAttribute("data-align") || "inline",
            caption: figcaption ? figcaption.textContent : "",
          };
        },
      },
      {
        tag: "img[src]",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const w = HTMLAttributes.width;
    const h = HTMLAttributes.height;
    const src = HTMLAttributes.src;

    const wVal = parseFloat(w);
    const hVal = parseFloat(h);
    let isImgIcon = false;
    if (!isNaN(wVal) && wVal > 0 && wVal <= 48) isImgIcon = true;
    else if (!isNaN(hVal) && hVal > 0 && hVal <= 48) isImgIcon = true;
    else if (src && (
      src.includes("icon") ||
      src.includes("logo") ||
      src.includes("star") ||
      src.includes("clock") ||
      src.includes("rating") ||
      src.includes("time") ||
      src.includes("calendar") ||
      src.includes("map-pin") ||
      src.startsWith("data:image/svg")
    )) {
      isImgIcon = true;
    }

    if (HTMLAttributes.caption) {
      return [
        "figure",
        {
          class: "editor-figure",
          "data-align": HTMLAttributes["data-align"] || "inline",
          style: HTMLAttributes.style
        },
        ["img", {
          ...HTMLAttributes,
          class: undefined,
          "data-align": undefined,
          caption: undefined,
          style: `width: 100%; height: 100%; object-fit: ${isImgIcon ? "fill" : "cover"};`
        }],
        ["figcaption", { class: "image-caption" }, HTMLAttributes.caption],
      ];
    }
    return [
      "img",
      {
        ...HTMLAttributes,
        "data-align": HTMLAttributes["data-align"] || "inline"
      }
    ];
  },

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute("src"),
        renderHTML: (attributes) => {
          if (!attributes.src) return {};
          return { src: attributes.src };
        },
      },
      alt: {
        default: null,
        parseHTML: (element) => element.getAttribute("alt"),
        renderHTML: (attributes) => {
          if (!attributes.alt) return {};
          return { alt: attributes.alt };
        },
      },
      title: {
        default: null,
        parseHTML: (element) => element.getAttribute("title"),
        renderHTML: (attributes) => {
          if (!attributes.title) return {};
          return { title: attributes.title };
        },
      },
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute("width") || element.style.width,
        renderHTML: (attributes) => {
          if (!attributes.width) return {};
          return { width: attributes.width };
        },
      },
      height: {
        default: null,
        parseHTML: (element) => element.getAttribute("height") || element.style.height,
        renderHTML: (attributes) => {
          if (!attributes.height) return {};
          return { height: attributes.height };
        },
      },
      alignment: {
        default: "inline",
        parseHTML: (element) => {
          if (element.getAttribute("data-align")) return element.getAttribute("data-align");
          if (element.style.float === "left") return "left";
          if (element.style.float === "right") return "right";
          if (element.style.display === "block" || element.style.margin?.includes("auto")) return "center";
          return "inline";
        },
        renderHTML: (attributes) => {
          return { "data-align": attributes.alignment || "inline" };
        },
      },
      caption: {
        default: "",
        parseHTML: (element) => {
          if (element.tagName.toLowerCase() === "figure") {
            const fig = element.querySelector("figcaption");
            return fig ? fig.textContent : "";
          }
          return element.getAttribute("data-caption") || "";
        },
        renderHTML: (attributes) => {
          if (!attributes.caption) return {};
          return { "data-caption": attributes.caption };
        },
      },
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute("style"),
        renderHTML: (attributes) => {
          const w = attributes.width;
          const h = attributes.height || "auto";
          const align = attributes.alignment || "inline";

          // Parse existing styles to preserve custom properties
          let styleParts = [];
          const existingStyle = attributes.style || "";
          if (existingStyle) {
            existingStyle.split(";").forEach((part) => {
              const trimmed = part.trim();
              if (!trimmed) return;
              const colonIndex = trimmed.indexOf(":");
              if (colonIndex === -1) return;
              const prop = trimmed.substring(0, colonIndex).trim().toLowerCase();
              // Skip properties that we manage dynamically
              if ([
                "width", "height", "position", "z-index", "float",
                "margin", "margin-top", "margin-bottom", "margin-left", "margin-right",
                "max-width", "display", "clear", "vertical-align"
              ].includes(prop)) {
                return;
              }
              styleParts.push(trimmed);
            });
          }
          // Append managed properties
          if (w) {
            const finalW = String(w).endsWith("px") || String(w).endsWith("%") ? w : `${w}px`;
            styleParts.push(`width: ${finalW}`);
          }
          if (h) {
            const finalH = String(h).endsWith("px") || String(h).endsWith("%") || h === "auto" ? h : `${h}px`;
            styleParts.push(`height: ${finalH}`);
          }
          styleParts.push("position: relative");
          styleParts.push("z-index: 1");

          // Determine if it is an icon to decide object-fit styling
          const wVal = parseFloat(w);
          const hVal = parseFloat(h);
          let isImgIcon = false;
          if (!isNaN(wVal) && wVal > 0 && wVal <= 48) isImgIcon = true;
          else if (!isNaN(hVal) && hVal > 0 && hVal <= 48) isImgIcon = true;
          else if (attributes.src && (
            attributes.src.includes("icon") ||
            attributes.src.includes("logo") ||
            attributes.src.includes("star") ||
            attributes.src.includes("clock") ||
            attributes.src.includes("rating") ||
            attributes.src.includes("time") ||
            attributes.src.includes("calendar") ||
            attributes.src.includes("map-pin") ||
            attributes.src.startsWith("data:image/svg")
          )) {
            isImgIcon = true;
          }
          styleParts.push(`object-fit: ${isImgIcon ? "fill" : "cover"}`);

          if (align === "left") {
            styleParts.push("float: left");
            styleParts.push("margin: 4px 20px 16px 0");
            styleParts.push("max-width: 50%");
          } else if (align === "right") {
            styleParts.push("float: right");
            styleParts.push("margin: 4px 0 16px 20px");
            styleParts.push("max-width: 50%");
          } else if (align === "center") {
            styleParts.push("display: block");
            styleParts.push("margin: 16px auto");
            styleParts.push("max-width: 100%");
            styleParts.push("clear: both");
          } else if (align === "inline") {
            styleParts.push("display: inline-block");
            styleParts.push("vertical-align: middle");
            styleParts.push("margin: 8px");
            styleParts.push("max-width: 100%");
          }

          return { style: styleParts.join("; ") + ";" };
        },
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImage);
  },
});

// Custom Iframe Node for Map embedding (pure HTML rendering with pointer-events: none in editor)
const Iframe = Node.create({
  name: "iframe",
  group: "block",
  selectable: true,
  draggable: true,
  atom: true,

  addAttributes() {
    return {
      src: { default: null },
      width: { default: "100%" },
      height: { default: 400 },
      style: { default: "border:0;width:100%;" },
      allowfullscreen: { default: "allowfullscreen" },
      loading: { default: "lazy" },
      referrerpolicy: { default: "no-referrer-when-downgrade" },
    };
  },

  parseHTML() {
    return [
      {
        tag: "iframe",
        getAttrs: (node) => ({
          src: node.getAttribute("src"),
          width: node.getAttribute("width"),
          height: node.getAttribute("height"),
          style: node.getAttribute("style"),
          allowfullscreen: node.getAttribute("allowfullscreen"),
          loading: node.getAttribute("loading"),
          referrerpolicy: node.getAttribute("referrerpolicy"),
        }),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const width = HTMLAttributes.width || "100%";
    const height = HTMLAttributes.height || "400";
    const heightStyle = String(height).endsWith("px") || String(height).endsWith("%") ? height : `${height}px`;
    const styleAttr = `border:0;width:${width} !important;height:${heightStyle} !important;pointer-events:none;`;
    return ["iframe", {
      ...HTMLAttributes,
      style: styleAttr
    }];
  },
});

const buildMapEmbedUrl = (projectUrl) => {
  return `https://web.geolibre.app/?url=${encodeURIComponent(projectUrl)}&maponly`;
};

const normalizeProjectUrl = (rawUrl) => {
  try {
    const parsed = new URL(rawUrl);
    if (
      parsed.hostname === "share.geolibre.app" &&
      !parsed.pathname.toLowerCase().endsWith(".geolibre.json")
    ) {
      parsed.pathname = parsed.pathname.replace(/\/+$/, "") + ".geolibre.json";
      return parsed.toString();
    }
  } catch {
    // Not a valid absolute URL — leave it untouched and let GeoLibre report the problem.
  }
  return rawUrl;
};

const injectCenterMarker = (projectJson) => {
  const currentBasemap = projectJson?.basemapStyleUrl || "";
  const isInvalidStyle =
    !currentBasemap ||
    currentBasemap.includes("demotiles.maplibre.org") ||
    currentBasemap.includes("cartocdn.com") ||
    currentBasemap.includes("mapbox.com") ||
    currentBasemap.includes("maptiler.com");

  const updatedProject = {
    ...projectJson,
    name: projectJson.name ? `${projectJson.name}-${Date.now()}` : `Map-${Date.now()}`,
    basemapStyleUrl: isInvalidStyle
      ? "https://tiles.openfreemap.org/styles/liberty"
      : currentBasemap,
    basemapVisible: projectJson.basemapVisible !== undefined ? projectJson.basemapVisible : true,
    basemapOpacity: projectJson.basemapOpacity !== undefined ? projectJson.basemapOpacity : 1,
  };

  const center = updatedProject?.mapView?.center;
  if (!Array.isArray(center) || center.length !== 2) {
    return updatedProject;
  }

  const alreadyHasMarker = (updatedProject.layers || []).some(
    (layer) => layer.name === "Location Marker"
  );
  if (alreadyHasMarker) {
    return updatedProject;
  }

  const markerLayer = {
    id: typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `marker-${Date.now()}`,
    name: "Location Marker",
    type: "geojson",
    source: { type: "geojson" },
    visible: true,
    opacity: 1,
    style: {
      minZoom: 0,
      maxZoom: 24,
      fillColor: "#e11d48",
      strokeColor: "#ffffff",
      strokeWidth: 2,
      fillOpacity: 1,
      circleRadius: 8,
    },
    metadata: {},
    geojson: {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          properties: {},
          geometry: { type: "Point", coordinates: center },
        },
      ],
    },
  };

  return {
    ...updatedProject,
    layers: [...(updatedProject.layers || []), markerLayer],
  };
};

const uploadGeoLibreProjectFile = async (file) => {
  console.log("Uploading file:", file.name, "size:", file.size);
  let uploadFile = file;
  let uploadName = file.name;
  if (!uploadName.toLowerCase().endsWith(".json")) {
    uploadName += ".json";
  }

  try {
    const text = await file.text();
    console.log("File content read successfully. Parsing JSON...");
    const projectJson = JSON.parse(text);
    console.log("JSON parsed successfully:", projectJson);
    const withMarker = injectCenterMarker(projectJson);
    console.log("Injected center marker:", withMarker);
    uploadFile = new File([JSON.stringify(withMarker)], uploadName, {
      type: "application/json",
    });
  } catch (err) {
    console.warn("Could not parse/process JSON, uploading original file. Error:", err);
    if (uploadFile.name !== uploadName) {
      uploadFile = new File([uploadFile], uploadName, {
        type: "application/json",
      });
    }
  }

  const formData = new FormData();
  formData.append("file", uploadFile);
  const token = localStorage.getItem("token");

  console.log("Sending POST request to:", `${apiUrl}/api/media/upload/maps?type=2`);
  const response = await axios.post(
    `${apiUrl}/api/media/upload/maps?type=2`,
    formData,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  console.log("Upload response received:", response.data);
  const key = response?.data?.data?.[0];
  if (!key) {
    throw new Error("Upload succeeded but no file URL was returned");
  }
  return `${bucketUrl}${key}`;
};

const uploadImageFile = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const token = localStorage.getItem("token");

  const response = await axios.post(
    `${apiUrl}/api/media/media?type=2`,
    formData,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  const key = response?.data?.data?.[0];
  if (!key) {
    throw new Error("Upload succeeded but no file URL was returned");
  }
  return `${bucketUrl}${key}`;
};

const activeUploads = new Set();

const base64ToFile = (base64String, filename = "pasted-image.png") => {
  try {
    const arr = base64String.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  } catch (e) {
    console.error("Failed to convert base64 to file", e);
    return null;
  }
};

const urlToFile = async (url, filename = "pasted-image.png") => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], filename, { type: blob.type });
  } catch (e) {
    console.warn("Direct fetch failed, trying canvas fallback for:", url, e);
    return new Promise((resolve) => {
      const img = new window.Image();
      img.src = url;
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(new File([blob], filename, { type: blob.type || "image/png" }));
            } else {
              resolve(null);
            }
          }, "image/png");
        } catch (err) {
          console.error("Canvas taint blocked image conversion:", err);
          resolve(null);
        }
      };
      img.onerror = () => {
        resolve(null);
      };
    });
  }
};

const extractProxyUrl = (url) => {
  try {
    if (url.includes("/api/image-proxy?url=")) {
      const parts = url.split("/api/image-proxy?url=");
      const proxyTarget = decodeURIComponent(parts[1]);
      return extractProxyUrl(proxyTarget);
    }
  } catch (e) {
    // ignore
  }
  return url;
};

const cleanLocalhostUrl = (url) => {
  try {
    const parsed = new URL(url);
    if (parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1") {
      return parsed.pathname + parsed.search + parsed.hash;
    }
  } catch (e) {
    // ignore
  }
  return url;
};

const uploadAllLocalImages = (editor) => {
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === "image") {
      const src = node.attrs.src;
      if (src) {
        // Extract original S3 URL if wrapped in proxy
        const cleanedSrc = extractProxyUrl(src);
        if (cleanedSrc !== src) {
          editor.commands.command(({ tr }) => {
            tr.setNodeMarkup(pos, undefined, {
              ...node.attrs,
              src: cleanedSrc,
            });
            return true;
          });
          return;
        }

        if (activeUploads.has(src)) return;

        const isBase64 = src.startsWith("data:image/");
        const isLocalhost = src.includes("localhost") || src.includes("127.0.0.1");
        const isLocalFile = src.startsWith("file:///");

        if (isLocalhost) {
          const relativeUrl = cleanLocalhostUrl(src);
          activeUploads.add(src);

          urlToFile(src)
            .then(async (file) => {
              if (file) {
                const uploadedUrl = await uploadImageFile(file);
                editor.commands.command(({ tr }) => {
                  tr.setNodeMarkup(pos, undefined, {
                    ...node.attrs,
                    src: uploadedUrl,
                  });
                  return true;
                });
              } else {
                editor.commands.command(({ tr }) => {
                  tr.setNodeMarkup(pos, undefined, {
                    ...node.attrs,
                    src: relativeUrl,
                  });
                  return true;
                });
              }
              activeUploads.delete(src);
            })
            .catch(() => {
              editor.commands.command(({ tr }) => {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  src: relativeUrl,
                });
                return true;
              });
              activeUploads.delete(src);
            });
          return;
        }

        if (isBase64 || isLocalFile) {
          activeUploads.add(src);

          const uploadPromise = isBase64
            ? Promise.resolve(base64ToFile(src))
            : urlToFile(src);

          uploadPromise
            .then(async (file) => {
              if (!file) {
                activeUploads.delete(src);
                return;
              }
              const uploadedUrl = await uploadImageFile(file);
              editor.commands.command(({ tr }) => {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  src: uploadedUrl,
                });
                return true;
              });
              activeUploads.delete(src);
            })
            .catch((err) => {
              activeUploads.delete(src);
              console.error("Failed to upload local image:", err);
            });
        }
      }
    }
  });
};

const presetColors = [
  // Row 1: Light pastels
  "#d1e7dd", "#fff3cd", "#f8d7da", "#e2e3e5", "#cff4fc",
  // Row 2: Standard vibrant colors
  "#198754", "#ffc107", "#dc3545", "#6c757d", "#0dcaf0",
  // Row 3: Mid tones
  "#0f5132", "#664d03", "#842029", "#2b303a", "#087990",
  // Row 4: Darks
  "#212529", "#adb5bd", "#e9ecef", "#f8f9fa", "#ffffff"
];

const TinyMceEditor = ({
  value,
  onChange,
  editorRef,
  height = 400,
  enableMap = true,
}) => {
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  // Modals UI states
  const [activeMenu, setActiveMenu] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [showTableModal, setShowTableModal] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [tableHeaders, setTableHeaders] = useState(true);
  const [tableBorderless, setTableBorderless] = useState(false);

  // Right-click context menu state
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, isImage: false, isTable: false });

  // Image size & alignment modal states
  const [showImageModal, setShowImageModal] = useState(false);
  const [wasImageModalOpen, setWasImageModalOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [imageAlt, setImageAlt] = useState("");
  const [imageWidth, setImageWidth] = useState("");
  const [imageHeight, setImageHeight] = useState("");
  const [imageAlign, setImageAlign] = useState("center");
  const [isEditingImage, setIsEditingImage] = useState(false);
  const [editingImagePos, setEditingImagePos] = useState(null);
  const savedSelectionRef = useRef(null);

  // Map modal states
  const [showMapModal, setShowMapModal] = useState(false);
  const [mapFile, setMapFile] = useState(null);
  const [mapUrl, setMapUrl] = useState("");
  const [mapWidth, setMapWidth] = useState("100%");
  const [mapHeight, setMapHeight] = useState("400");
  const [isUploadingMap, setIsUploadingMap] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
      CustomImage,
      CustomDiv,
      CustomTable.configure({
        resizable: true,
        HTMLAttributes: {
          style: "border-collapse: collapse; width: 100%; border: 1px solid #dee2e6; margin-bottom: 1rem;",
        },
      }),
      TableRow,
      TableCell.configure({
        HTMLAttributes: {
          style: "border: 1px solid #dee2e6; padding: 8px; min-width: 1em;",
        },
      }),
      TableHeader.configure({
        HTMLAttributes: {
          style: "border: 1px solid #dee2e6; padding: 8px; background-color: #f8f9fa; font-weight: bold; min-width: 1em;",
        },
      }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Iframe,
      Subscript,
      Superscript,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      FontFamily,
      FontSize,
      LineHeight,
      Indent,
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
      uploadAllLocalImages(editor);
    },
    editorProps: {
      handleDrop: (view, event, slice, moved) => {
        if (!moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]) {
          const file = event.dataTransfer.files[0];
          if (file.type.startsWith("image/")) {
            event.preventDefault();
            const { schema } = view.state;
            const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY });

            uploadImageFile(file)
              .then((url) => {
                const node = schema.nodes.image.create({ src: url, alignment: "inline" });
                const transaction = view.state.tr.insert(coordinates.pos, node);
                view.dispatch(transaction);
                toast.success("Image uploaded successfully");
              })
              .catch((err) => {
                toast.error("Failed to upload image: " + err.message);
              });
            return true;
          }
        }
        return false;
      },
      handlePaste: (view, event) => {
        const clipboardData = event.clipboardData;
        if (!clipboardData) return false;

        const files = clipboardData.files;
        const html = clipboardData.getData("text/html");

        if (files && files.length > 0 && html) {
          event.preventDefault();
          const uploadPromises = Array.from(files).map(file => uploadImageFile(file));

          Promise.all(uploadPromises)
            .then((uploadedUrls) => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, "text/html");
              const imgs = doc.querySelectorAll("img");

              let fileIndex = 0;
              imgs.forEach((img) => {
                const src = img.getAttribute("src") || "";
                if (src.startsWith("file:///") || src.startsWith("blob:") || src.startsWith("data:") || !src.startsWith("http")) {
                  if (uploadedUrls[fileIndex]) {
                    img.setAttribute("src", uploadedUrls[fileIndex]);
                    fileIndex++;
                  }
                }
              });

              const pmParser = ProseMirrorDOMParser.fromSchema(view.state.schema);
              const slice = pmParser.parseSlice(doc.body);
              const transaction = view.state.tr.replaceSelection(slice);
              view.dispatch(transaction);
              toast.success("Images uploaded and pasted successfully");
            })
            .catch((err) => {
              console.error("Failed to upload pasted files:", err);
              toast.error("Failed to upload pasted images");
            });

          return true;
        }

        if (files && files.length > 0 && !html) {
          const file = files[0];
          if (file.type.startsWith("image/")) {
            event.preventDefault();
            const { schema } = view.state;

            uploadImageFile(file)
              .then((url) => {
                const node = schema.nodes.image.create({ src: url, alignment: "inline" });
                const transaction = view.state.tr.replaceSelectionWith(node);
                view.dispatch(transaction);
                toast.success("Image uploaded successfully");
              })
              .catch((err) => {
                toast.error("Failed to upload image: " + err.message);
              });
            return true;
          }
        }
        return false;
      }
    },
  });

  // Attach right-click context menu listener & double-click image listener
  useEffect(() => {
    if (!editor) return;
    const dom = editor.view.dom;

    const handleContextMenu = (e) => {
      e.preventDefault();
      const imgEl = e.target.closest("img");
      const isImg = !!imgEl || editor.isActive("image");
      const isTbl = !!e.target.closest("table") || editor.isActive("table");

      if (imgEl) {
        const src = imgEl.getAttribute("src") || "";
        const alt = imgEl.getAttribute("alt") || "";
        const width = imgEl.getAttribute("width") || imgEl.style.width || "";
        const height = imgEl.getAttribute("height") || imgEl.style.height || "";
        const style = imgEl.getAttribute("style") || "";
        const dataAlign = imgEl.getAttribute("data-align");

        let align = dataAlign || "center";
        if (!dataAlign) {
          if (style.includes("float: left") || style.includes("float:left")) align = "left";
          else if (style.includes("float: right") || style.includes("float:right")) align = "right";
          else if (style.includes("margin: 16px auto") || style.includes("display: block")) align = "center";
          else if (style.includes("display: inline-block")) align = "inline";
        }

        setImageUrl(src);
        setImageAlt(alt);
        setImageWidth(width ? String(width).replace("px", "") : "350");
        setImageHeight(height ? String(height).replace("px", "") : "auto");
        setImageAlign(align);
        setIsEditingImage(true);
        try {
          const pos = editor.view.posAtDOM(imgEl, 0);
          setEditingImagePos(pos);
        } catch (e) {
          console.warn("Could not determine image position on context menu", e);
        }
      }

      setContextMenu({
        visible: true,
        x: e.clientX,
        y: e.clientY,
        isImage: isImg,
        isTable: isTbl,
      });
    };

    const handleDblClick = (e) => {
      const img = e.target.closest("img");
      if (img) {
        const src = img.getAttribute("src") || "";
        const alt = img.getAttribute("alt") || "";
        const width = img.getAttribute("width") || img.style.width || "";
        const height = img.getAttribute("height") || img.style.height || "";
        const style = img.getAttribute("style") || "";
        const dataAlign = img.getAttribute("data-align");

        let align = dataAlign || "center";
        if (!dataAlign) {
          if (style.includes("float: left") || style.includes("float:left")) align = "left";
          else if (style.includes("float: right") || style.includes("float:right")) align = "right";
          else if (style.includes("margin: 16px auto") || style.includes("display: block")) align = "center";
          else if (style.includes("display: inline-block")) align = "inline";
        }

        setImageUrl(src);
        setImageAlt(alt);
        setImageWidth(width ? String(width).replace("px", "") : "350");
        setImageHeight(height ? String(height).replace("px", "") : "auto");
        setImageAlign(align);
        setIsEditingImage(true);
        setShowImageModal(true);
        try {
          const pos = editor.view.posAtDOM(img, 0);
          setEditingImagePos(pos);
        } catch (e) {
          console.warn("Could not determine image position on double click", e);
        }
      }
    };

    const handleClickOutside = () => {
      setContextMenu((prev) => (prev.visible ? { ...prev, visible: false } : prev));
    };

    dom.addEventListener("contextmenu", handleContextMenu);
    dom.addEventListener("dblclick", handleDblClick);
    window.addEventListener("click", handleClickOutside);
    window.addEventListener("scroll", handleClickOutside, true);

    return () => {
      dom.removeEventListener("contextmenu", handleContextMenu);
      dom.removeEventListener("dblclick", handleDblClick);
      window.removeEventListener("click", handleClickOutside);
      window.removeEventListener("scroll", handleClickOutside, true);
    };
  }, [editor]);

  // Watch exterior value changes
  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  // Expose methods to editorRef
  useImperativeHandle(editorRef, () => ({
    setContent: (content) => {
      editor?.commands.setContent(content);
    },
    getEditor: () => editor,
  }));

  const handleOpenBrowseFromImageModal = () => {
    setWasImageModalOpen(true);
    setShowImageModal(false);
    setShowMediaPicker(true);
  };

  const handleMediaSelect = (url) => {
    if (url) {
      setImageUrl(url);
      if (editor) {
        if (editingImagePos !== null) {
          editor.commands.command(({ tr }) => {
            const node = tr.doc.nodeAt(editingImagePos);
            if (node && node.type.name === "image") {
              tr.setNodeMarkup(editingImagePos, undefined, {
                ...node.attrs,
                src: url,
              });
              return true;
            }
            return false;
          });
        } else if (editor.isActive("image") || isEditingImage) {
          editor.chain().focus().updateAttributes("image", { src: url }).run();
        }
      }
    }
    setShowMediaPicker(false);
    if (wasImageModalOpen) {
      setShowImageModal(true);
      setWasImageModalOpen(false);
    }
  };

  const handleCloseMediaPicker = () => {
    setShowMediaPicker(false);
    if (wasImageModalOpen) {
      setShowImageModal(true);
      setWasImageModalOpen(false);
    }
  };

  const handleOpenImageModal = () => {
    // Save the current cursor position before opening the modal
    if (editor) {
      savedSelectionRef.current = {
        from: editor.state.selection.from,
        to: editor.state.selection.to,
      };
    }
    if (editor && editor.isActive("image")) {
      const attrs = editor.getAttributes("image");
      setImageUrl(attrs.src || "");
      setImageAlt(attrs.alt || "");
      setImageWidth(attrs.width ? String(attrs.width).replace("px", "") : "350");
      setImageHeight(attrs.height ? String(attrs.height).replace("px", "") : "auto");

      let align = attrs.alignment || "left";
      if (attrs.style) {
        if (attrs.style.includes("float: left") || attrs.style.includes("float:left")) align = "left";
        else if (attrs.style.includes("float: right") || attrs.style.includes("float:right")) align = "right";
        else if (attrs.style.includes("margin: 16px auto") || attrs.style.includes("display: block")) align = "center";
        else if (attrs.style.includes("display: inline-block")) align = "inline";
      }
      setImageAlign(align);
      setIsEditingImage(true);

      // Find the position of the selected image
      const { selection } = editor.state;
      let pos = null;
      if (selection.node && selection.node.type.name === "image") {
        pos = selection.from;
      } else {
        editor.state.doc.nodesBetween(selection.from, selection.to, (node, nodePos) => {
          if (node.type.name === "image") {
            pos = nodePos;
            return false;
          }
        });
      }
      if (pos !== null) {
        setEditingImagePos(pos);
      }
    } else {
      setImageUrl("");
      setImageAlt("");
      setImageWidth("350");
      setImageHeight("auto");
      setImageAlign("left");
      setIsEditingImage(false);
      setEditingImagePos(null);
    }
    setShowImageModal(true);
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setImageUrl("");
    setImageAlt("");
    setImageWidth("");
    setImageHeight("");
    setImageAlign("left");
    setIsEditingImage(false);
    setEditingImagePos(null);
  };

  const handleInsertImage = () => {
    if (!imageUrl) {
      handleCloseImageModal();
      return;
    }

    const finalWidth = imageWidth ? (String(imageWidth).endsWith("px") || String(imageWidth).endsWith("%") ? imageWidth : `${imageWidth}px`) : "350px";
    const finalHeight = imageHeight ? (String(imageHeight).endsWith("px") || String(imageHeight).endsWith("%") || imageHeight === "auto" ? imageHeight : `${imageHeight}px`) : "auto";

    const hSuffix = finalHeight === "auto" ? "height: auto;" : "";
    let styleStr = `width: ${finalWidth}; height: ${finalHeight}; position: relative; z-index: 1; `;
    if (imageAlign === "left") {
      styleStr += `float: left; margin: 4px 20px 16px 0; max-width: 50%; ${hSuffix}`;
    } else if (imageAlign === "right") {
      styleStr += `float: right; margin: 4px 0 16px 20px; max-width: 50%; ${hSuffix}`;
    } else if (imageAlign === "center") {
      styleStr += `display: block; margin: 16px auto; max-width: 100%; ${hSuffix} clear: both;`;
    } else if (imageAlign === "inline") {
      styleStr += `display: inline-block; vertical-align: middle; margin: 8px; max-width: 100%; ${hSuffix}`;
    }

    if (editingImagePos !== null) {
      editor.commands.command(({ tr }) => {
        const node = tr.doc.nodeAt(editingImagePos);
        if (node && node.type.name === "image") {
          tr.setNodeMarkup(editingImagePos, undefined, {
            ...node.attrs,
            src: imageUrl,
            alt: imageAlt,
            width: finalWidth,
            height: finalHeight,
            alignment: imageAlign,
            style: styleStr,
          });
          return true;
        }
        return false;
      });
      setEditingImagePos(null);
    } else if (editor.isActive("image") || isEditingImage) {
      editor.chain().focus().updateAttributes("image", {
        src: imageUrl,
        alt: imageAlt,
        width: finalWidth,
        height: finalHeight,
        alignment: imageAlign,
        style: styleStr,
      }).run();
    } else {
      // Restore saved cursor position before inserting, so image goes where the cursor was
      const savedPos = savedSelectionRef.current;
      if (savedPos) {
        editor.commands.setTextSelection(savedPos.from);
      }
      editor.chain().focus().setImage({
        src: imageUrl,
        alt: imageAlt,
        width: finalWidth,
        height: finalHeight,
        alignment: imageAlign,
        style: styleStr,
      }).run();
      savedSelectionRef.current = null;
    }

    handleCloseImageModal();
  };

  const setQuickImageAlign = (align) => {
    if (!editor || !editor.isActive("image")) return;
    const attrs = editor.getAttributes("image");
    const width = attrs.width ? (String(attrs.width).endsWith("px") || String(attrs.width).endsWith("%") ? attrs.width : `${attrs.width}px`) : "350px";
    const height = attrs.height ? (String(attrs.height).endsWith("px") || String(attrs.height).endsWith("%") || attrs.height === "auto" ? attrs.height : `${attrs.height}px`) : "auto";

    const hSuffix = height === "auto" ? "height: auto;" : "";
    let styleStr = `width: ${width}; height: ${height}; position: relative; z-index: 1; `;
    if (align === "left") {
      styleStr += `float: left; margin: 4px 20px 16px 0; max-width: 50%; ${hSuffix}`;
    } else if (align === "right") {
      styleStr += `float: right; margin: 4px 0 16px 20px; max-width: 50%; ${hSuffix}`;
    } else if (align === "center") {
      styleStr += `display: block; margin: 16px auto; max-width: 100%; ${hSuffix} clear: both;`;
    } else if (align === "inline") {
      styleStr += `display: inline-block; vertical-align: middle; margin: 8px; max-width: 100%; ${hSuffix}`;
    }

    editor.chain().focus().updateAttributes("image", {
      alignment: align,
      style: styleStr,
    }).run();
  };

  const handleTextAlign = (align) => {
    if (!editor) return;
    if (editor.isActive("image")) {
      setQuickImageAlign(align);
    } else {
      editor.chain().focus().setTextAlign(align).run();
    }
  };

  const isTextAlignActive = (align) => {
    if (!editor) return false;
    if (editor.isActive("image")) {
      const attrs = editor.getAttributes("image");
      return attrs.alignment === align;
    }
    return editor.isActive({ textAlign: align });
  };

  const handleOpenLinkModal = () => {
    if (!editor) return;
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, " ");
    const currentHref = editor.getAttributes("link").href || "";

    setLinkUrl(currentHref);
    setLinkText(selectedText || "");
    setShowLinkModal(true);
  };

  const handleInsertLink = () => {
    let trimmedUrl = linkUrl.trim();
    if (!trimmedUrl) {
      editor?.chain().focus().unsetLink().run();
      setShowLinkModal(false);
      setLinkUrl("");
      setLinkText("");
      return;
    }

    if (!/^https?:\/\//i.test(trimmedUrl) && !trimmedUrl.startsWith("mailto:") && !trimmedUrl.startsWith("/")) {
      trimmedUrl = "https://" + trimmedUrl;
    }

    const { from, to } = editor.state.selection;
    const hasSelection = from !== to;

    if (hasSelection) {
      editor.chain().focus().extendMarkRange("link").setLink({ href: trimmedUrl }).run();
    } else {
      const displayText = linkText.trim() || trimmedUrl;
      editor.chain().focus().insertContent(`<a href="${trimmedUrl}">${displayText}</a>`).run();
    }

    setShowLinkModal(false);
    setLinkUrl("");
    setLinkText("");
  };

  const handleInsertTable = () => {
    if (tableBorderless) {
      let cellsHtml = "";
      const colWidth = (100 / tableCols).toFixed(2);
      for (let i = 0; i < tableCols; i++) {
        cellsHtml += `<td style="width: ${colWidth}%"><p></p></td>`;
      }
      let rowsHtml = "";
      for (let j = 0; j < tableRows; j++) {
        rowsHtml += `<tr>${cellsHtml}</tr>`;
      }
      const tableHtml = `<table class="borderless" border="0"><tbody>${rowsHtml}</tbody></table>`;
      editor.chain().focus().insertContent(tableHtml).run();
    } else {
      editor.chain().focus().insertTable({ rows: tableRows, cols: tableCols, withHeaderRow: tableHeaders }).run();
    }
    setShowTableModal(false);
  };

  const handleInsertMap = async () => {
    console.log("handleInsertMap started. mapUrl:", mapUrl, "mapFile:", mapFile, "width:", mapWidth, "height:", mapHeight);
    const trimmedUrl = mapUrl.trim();
    const widthVal = mapWidth || "100%";
    const heightVal = mapHeight || "400";
    const heightStyle = heightVal.endsWith("px") || heightVal.endsWith("%") ? heightVal : `${heightVal}px`;

    if (trimmedUrl) {
      console.log("Using URL input for map:", trimmedUrl);
      const src = buildMapEmbedUrl(normalizeProjectUrl(trimmedUrl));
      console.log("Inserting iframe with src:", src);
      editor.chain().focus().insertContent(`<iframe src="${src}" width="${widthVal}" height="${heightVal}" style="border:0;width:${widthVal};height:${heightStyle};" loading="lazy" referrerpolicy="no-referrer-when-downgrade" allowfullscreen="allowfullscreen"></iframe>`).run();
      setShowMapModal(false);
      setMapUrl("");
      setMapFile(null);
      return;
    }

    if (!mapFile) {
      console.warn("No map file selected and map URL is empty.");
      toast.error("Choose a GeoLibre project file to upload, or paste a project URL");
      return;
    }

    const nameLower = mapFile.name.toLowerCase();
    if (!nameLower.endsWith(".json") && !nameLower.endsWith(".geolibre")) {
      console.warn("Invalid file extension:", mapFile.name);
      toast.error("Please upload a GeoLibre project file (.geolibre.json)");
      return;
    }

    setIsUploadingMap(true);
    try {
      console.log("Triggering file upload...");
      const uploadedUrl = await uploadGeoLibreProjectFile(mapFile);
      console.log("Upload successful! URL:", uploadedUrl);
      const src = buildMapEmbedUrl(uploadedUrl);
      console.log("Inserting iframe with src:", src);
      editor.chain().focus().insertContent(`<iframe src="${src}" width="${widthVal}" height="${heightVal}" style="border:0;width:${widthVal};height:${heightStyle};" loading="lazy" referrerpolicy="no-referrer-when-downgrade" allowfullscreen="allowfullscreen"></iframe>`).run();
      setShowMapModal(false);
      setMapFile(null);
    } catch (error) {
      console.error("Map upload failed:", error);
      toast.error(error?.response?.data?.message || error.message || "Failed to upload the project file");
    } finally {
      setIsUploadingMap(false);
    }
  };

  if (!editor) return null;

  return (
    <>
      <div className="tiptap-editor-wrapper" style={{ height: height === "100%" ? "600px" : height, minHeight: "600px" }}>
        {/* TinyMCE-like Menubar */}
        <div className="tiptap-menu-bar" onMouseDown={(e) => { if (e.target.closest('.tiptap-menu-bar')) e.preventDefault(); }}>
          <div className="tiptap-menu-item">
            <button
              type="button"
              className="tiptap-menu-btn"
              onClick={() => setActiveMenu(activeMenu === "edit" ? null : "edit")}
            >
              Edit
            </button>
            {activeMenu === "edit" && (
              <div className="tiptap-dropdown-menu" onMouseLeave={() => setActiveMenu(null)}>
                <button
                  type="button"
                  className="tiptap-dropdown-item"
                  onClick={() => { editor.chain().focus().undo().run(); setActiveMenu(null); }}
                >
                  Undo
                </button>
                <button
                  type="button"
                  className="tiptap-dropdown-item"
                  onClick={() => { editor.chain().focus().redo().run(); setActiveMenu(null); }}
                >
                  Redo
                </button>
              </div>
            )}
          </div>

          <div className="tiptap-menu-item">
            <button
              type="button"
              className="tiptap-menu-btn"
              onClick={() => setActiveMenu(activeMenu === "format" ? null : "format")}
            >
              Format
            </button>
            {activeMenu === "format" && (
              <div className="tiptap-dropdown-menu" onMouseLeave={() => setActiveMenu(null)}>
                <button
                  type="button"
                  className="tiptap-dropdown-item"
                  onClick={() => { editor.chain().focus().toggleBold().run(); setActiveMenu(null); }}
                  style={{ fontWeight: editor.isActive("bold") ? "bold" : "normal" }}
                >
                  Bold (Ctrl+B)
                </button>
                <button
                  type="button"
                  className="tiptap-dropdown-item"
                  onClick={() => { editor.chain().focus().toggleItalic().run(); setActiveMenu(null); }}
                  style={{ fontStyle: editor.isActive("italic") ? "italic" : "normal" }}
                >
                  Italic (Ctrl+I)
                </button>
                <button
                  type="button"
                  className="tiptap-dropdown-item"
                  onClick={() => { editor.chain().focus().toggleUnderline().run(); setActiveMenu(null); }}
                  style={{ textDecoration: editor.isActive("underline") ? "underline" : "none" }}
                >
                  Underline (Ctrl+U)
                </button>
                <button
                  type="button"
                  className="tiptap-dropdown-item"
                  onClick={() => { editor.chain().focus().toggleStrike().run(); setActiveMenu(null); }}
                >
                  Strikethrough
                </button>
                <button
                  type="button"
                  className="tiptap-dropdown-item"
                  onClick={() => { editor.chain().focus().toggleSuperscript().run(); setActiveMenu(null); }}
                >
                  Superscript
                </button>
                <button
                  type="button"
                  className="tiptap-dropdown-item"
                  onClick={() => { editor.chain().focus().toggleSubscript().run(); setActiveMenu(null); }}
                >
                  Subscript
                </button>
                <button
                  type="button"
                  className="tiptap-dropdown-item"
                  onClick={() => { editor.chain().focus().toggleCode().run(); setActiveMenu(null); }}
                >
                  Code
                </button>

                <div style={{ height: "1px", backgroundColor: "#e9ecef", margin: "4px 0" }} />

                {/* Blocks Flyout */}
                <div className="tiptap-dropdown-item-wrapper">
                  <div className="tiptap-dropdown-item d-flex justify-content-between align-items-center">
                    <span>Blocks</span>
                    <span style={{ fontSize: "10px", color: "#888" }}>▶</span>
                  </div>
                  <div className="tiptap-submenu">
                    <button type="button" className="tiptap-dropdown-item" onClick={() => { editor.chain().focus().setParagraph().run(); setActiveMenu(null); }}>Paragraph</button>
                    <button type="button" className="tiptap-dropdown-item" onClick={() => { editor.chain().focus().toggleHeading({ level: 1 }).run(); setActiveMenu(null); }}>Heading 1</button>
                    <button type="button" className="tiptap-dropdown-item" onClick={() => { editor.chain().focus().toggleHeading({ level: 2 }).run(); setActiveMenu(null); }}>Heading 2</button>
                    <button type="button" className="tiptap-dropdown-item" onClick={() => { editor.chain().focus().toggleHeading({ level: 3 }).run(); setActiveMenu(null); }}>Heading 3</button>
                    <button type="button" className="tiptap-dropdown-item" onClick={() => { editor.chain().focus().toggleHeading({ level: 4 }).run(); setActiveMenu(null); }}>Heading 4</button>
                  </div>
                </div>

                {/* Fonts Flyout */}
                <div className="tiptap-dropdown-item-wrapper">
                  <div className="tiptap-dropdown-item d-flex justify-content-between align-items-center">
                    <span>Fonts</span>
                    <span style={{ fontSize: "10px", color: "#888" }}>▶</span>
                  </div>
                  <div className="tiptap-submenu" style={{ maxHeight: "250px", overflowY: "auto" }}>
                    {[
                      "Andale Mono", "Arial", "Arial Black", "Book Antiqua",
                      "Comic Sans MS", "Courier New", "Georgia", "Helvetica",
                      "Impact", "Symbol", "Tahoma", "Terminal", "Times New Roman",
                      "Trebuchet MS", "Verdana"
                    ].map((font) => (
                      <button
                        key={font}
                        type="button"
                        className="tiptap-dropdown-item"
                        style={{ fontFamily: font }}
                        onClick={() => { editor.chain().focus().setFontFamily(font).run(); setActiveMenu(null); }}
                      >
                        {font}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Font Sizes Flyout */}
                <div className="tiptap-dropdown-item-wrapper">
                  <div className="tiptap-dropdown-item d-flex justify-content-between align-items-center">
                    <span>Font sizes</span>
                    <span style={{ fontSize: "10px", color: "#888" }}>▶</span>
                  </div>
                  <div className="tiptap-submenu">
                    {["12px", "14px", "16px", "18px", "20px", "24px", "30px", "36px"].map((sz) => (
                      <button key={sz} type="button" className="tiptap-dropdown-item" onClick={() => { editor.chain().focus().setFontSize(sz).run(); setActiveMenu(null); }}>{sz}</button>
                    ))}
                  </div>
                </div>

                {/* Align Flyout */}
                <div className="tiptap-dropdown-item-wrapper">
                  <div className="tiptap-dropdown-item d-flex justify-content-between align-items-center">
                    <span>Align</span>
                    <span style={{ fontSize: "10px", color: "#888" }}>▶</span>
                  </div>
                  <div className="tiptap-submenu">
                    <button type="button" className="tiptap-dropdown-item" onClick={() => { handleTextAlign("left"); setActiveMenu(null); }}>Left</button>
                    <button type="button" className="tiptap-dropdown-item" onClick={() => { handleTextAlign("center"); setActiveMenu(null); }}>Center</button>
                    <button type="button" className="tiptap-dropdown-item" onClick={() => { handleTextAlign("right"); setActiveMenu(null); }}>Right</button>
                    <button type="button" className="tiptap-dropdown-item" onClick={() => { handleTextAlign("justify"); setActiveMenu(null); }}>Justify</button>
                  </div>
                </div>

                {/* Line Height Flyout */}
                <div className="tiptap-dropdown-item-wrapper">
                  <div className="tiptap-dropdown-item d-flex justify-content-between align-items-center">
                    <span>Line height</span>
                    <span style={{ fontSize: "10px", color: "#888" }}>▶</span>
                  </div>
                  <div className="tiptap-submenu">
                    {["1.0", "1.15", "1.5", "2.0", "2.5", "3.0"].map((lh) => (
                      <button key={lh} type="button" className="tiptap-dropdown-item" onClick={() => { editor.chain().focus().setLineHeight(lh).run(); setActiveMenu(null); }}>{lh}</button>
                    ))}
                  </div>
                </div>

                {/* Text Color Flyout */}
                <div className="tiptap-dropdown-item-wrapper">
                  <div className="tiptap-dropdown-item d-flex justify-content-between align-items-center">
                    <span>Text color</span>
                    <span style={{ fontSize: "10px", color: "#888" }}>▶</span>
                  </div>
                  <div className="tiptap-submenu" style={{ width: "150px", padding: "8px" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "4px", marginBottom: "8px" }}>
                      {presetColors.map((color) => (
                        <button
                          key={color}
                          type="button"
                          style={{
                            width: "20px",
                            height: "20px",
                            backgroundColor: color,
                            border: "1px solid #ced4da",
                            borderRadius: "3px",
                            cursor: "pointer",
                            padding: 0
                          }}
                          onClick={() => { editor.chain().focus().setColor(color).run(); setActiveMenu(null); }}
                          title={color}
                        />
                      ))}
                    </div>
                    <div className="d-flex align-items-center gap-2 pt-2 border-top">
                      <input
                        type="color"
                        onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
                        style={{ width: "20px", height: "20px", padding: 0, border: "none", cursor: "pointer", borderRadius: "3px" }}
                        title="Custom Color"
                      />
                      <span style={{ fontSize: "11px", color: "#495057" }}>Custom</span>
                      <button
                        type="button"
                        style={{
                          fontSize: "11px",
                          border: "none",
                          background: "none",
                          padding: 0,
                          color: "#dc3545",
                          marginLeft: "auto",
                          cursor: "pointer"
                        }}
                        onClick={() => { editor.chain().focus().unsetColor().run(); setActiveMenu(null); }}
                      >
                        Reset
                      </button>
                    </div>
                  </div>
                </div>

                {/* Background Color Flyout */}
                <div className="tiptap-dropdown-item-wrapper">
                  <div className="tiptap-dropdown-item d-flex justify-content-between align-items-center">
                    <span>Background color</span>
                    <span style={{ fontSize: "10px", color: "#888" }}>▶</span>
                  </div>
                  <div className="tiptap-submenu" style={{ width: "150px", padding: "8px" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "4px", marginBottom: "8px" }}>
                      {presetColors.map((color) => (
                        <button
                          key={color}
                          type="button"
                          style={{
                            width: "20px",
                            height: "20px",
                            backgroundColor: color,
                            border: "1px solid #ced4da",
                            borderRadius: "3px",
                            cursor: "pointer",
                            padding: 0
                          }}
                          onClick={() => { editor.chain().focus().setHighlight({ color }).run(); setActiveMenu(null); }}
                          title={color}
                        />
                      ))}
                    </div>
                    <div className="d-flex align-items-center gap-2 pt-2 border-top">
                      <input
                        type="color"
                        onChange={(e) => editor.chain().focus().setHighlight({ color: e.target.value }).run()}
                        style={{ width: "20px", height: "20px", padding: 0, border: "none", cursor: "pointer", borderRadius: "3px" }}
                        title="Custom Highlight"
                      />
                      <span style={{ fontSize: "11px", color: "#495057" }}>Custom</span>
                      <button
                        type="button"
                        style={{
                          fontSize: "11px",
                          border: "none",
                          background: "none",
                          padding: 0,
                          color: "#dc3545",
                          marginLeft: "auto",
                          cursor: "pointer"
                        }}
                        onClick={() => { editor.chain().focus().unsetHighlight().run(); setActiveMenu(null); }}
                      >
                        Reset
                      </button>
                    </div>
                  </div>
                </div>

                <div style={{ height: "1px", backgroundColor: "#e9ecef", margin: "4px 0" }} />

                <button
                  type="button"
                  className="tiptap-dropdown-item"
                  onClick={() => {
                    editor.chain().focus().clearNodes().unsetAllMarks().run();
                    setActiveMenu(null);
                  }}
                  style={{ color: "#dc3545" }}
                >
                  Clear formatting
                </button>
              </div>
            )}
          </div>

          <div className="tiptap-menu-item">
            <button
              type="button"
              className="tiptap-menu-btn"
              onClick={() => setActiveMenu(activeMenu === "insert" ? null : "insert")}
            >
              Insert
            </button>
            {activeMenu === "insert" && (
              <div className="tiptap-dropdown-menu" onMouseLeave={() => setActiveMenu(null)}>
                <button
                  type="button"
                  className="tiptap-dropdown-item"
                  onClick={() => { handleOpenImageModal(); setActiveMenu(null); }}
                >
                  Image...
                </button>
                <button
                  type="button"
                  className="tiptap-dropdown-item"
                  onClick={() => { setShowLinkModal(true); setActiveMenu(null); }}
                >
                  Link...
                </button>
                <button
                  type="button"
                  className="tiptap-dropdown-item"
                  onClick={() => { setShowTableModal(true); setActiveMenu(null); }}
                >
                  Table...
                </button>

                {/* Columns Layout Flyout */}
                <div className="tiptap-dropdown-item-wrapper">
                  <div className="tiptap-dropdown-item d-flex justify-content-between align-items-center">
                    <span>Columns</span>
                    <span style={{ fontSize: "10px", color: "#888", marginLeft: "8px" }}>▶</span>
                  </div>
                  <div className="tiptap-submenu">
                    <button
                      type="button"
                      className="tiptap-dropdown-item"
                      onClick={() => {
                        editor.chain().focus().insertContent('<div class="column-row" style="display: flex; gap: 20px;"><div class="column-col" style="flex: 1; min-width: 0;"><p>Column 1</p></div></div>').run();
                        setActiveMenu(null);
                      }}
                    >
                      1 Column (100%)
                    </button>
                    <button
                      type="button"
                      className="tiptap-dropdown-item"
                      onClick={() => {
                        editor.chain().focus().insertContent('<div class="column-row" style="display: flex; gap: 20px;"><div class="column-col" style="flex: 1; min-width: 0;"><p>Column 1</p></div><div class="column-col" style="flex: 1; min-width: 0;"><p>Column 2</p></div></div>').run();
                        setActiveMenu(null);
                      }}
                    >
                      2 Columns (50/50)
                    </button>
                    <button
                      type="button"
                      className="tiptap-dropdown-item"
                      onClick={() => {
                        editor.chain().focus().insertContent('<div class="column-row" style="display: flex; gap: 20px;"><div class="column-col" style="flex: 1; min-width: 0;"><p>Column 1</p></div><div class="column-col" style="flex: 1; min-width: 0;"><p>Column 2</p></div><div class="column-col" style="flex: 1; min-width: 0;"><p>Column 3</p></div></div>').run();
                        setActiveMenu(null);
                      }}
                    >
                      3 Columns (33/33/33)
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  className="tiptap-dropdown-item"
                  onClick={() => { editor.chain().focus().setHorizontalRule().run(); setActiveMenu(null); }}
                >
                  Horizontal Line
                </button>
                <button
                  type="button"
                  className="tiptap-dropdown-item"
                  onClick={() => { editor.chain().focus().toggleCodeBlock().run(); setActiveMenu(null); }}
                >
                  Code Block
                </button>
              </div>
            )}
          </div>

          {enableMap && (
            <div className="tiptap-menu-item">
              <button
                type="button"
                className="tiptap-menu-btn"
                onClick={() => setActiveMenu(activeMenu === "map" ? null : "map")}
              >
                Map
              </button>
              {activeMenu === "map" && (
                <div className="tiptap-dropdown-menu" onMouseLeave={() => setActiveMenu(null)}>
                  <button
                    type="button"
                    className="tiptap-dropdown-item"
                    onClick={() => { setShowMapModal(true); setActiveMenu(null); }}
                  >
                    Insert Map Location...
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Toolbar */}
        <div className="tiptap-toolbar" onMouseDown={(e) => e.preventDefault()}>
          <button
            type="button"
            className="tiptap-toolbar-btn"
            onClick={() => editor.chain().focus().undo().run()}
            title="Undo"
          >
            <Undo2 size={16} />
          </button>
          <button
            type="button"
            className="tiptap-toolbar-btn"
            onClick={() => editor.chain().focus().redo().run()}
            title="Redo"
          >
            <Redo2 size={16} />
          </button>

          <div className="tiptap-toolbar-divider" />

          <button
            type="button"
            className={`tiptap-toolbar-btn ${editor.isActive("bold") ? "is-active" : ""}`}
            onClick={() => editor.chain().focus().toggleBold().run()}
            title="Bold"
          >
            <Bold size={16} />
          </button>
          <button
            type="button"
            className={`tiptap-toolbar-btn ${editor.isActive("italic") ? "is-active" : ""}`}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            title="Italic"
          >
            <Italic size={16} />
          </button>
          <button
            type="button"
            className={`tiptap-toolbar-btn ${editor.isActive("underline") ? "is-active" : ""}`}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            title="Underline"
          >
            <UnderlineIcon size={16} />
          </button>

          <div className="tiptap-toolbar-divider" />

          <button
            type="button"
            className={`tiptap-toolbar-btn ${editor.isActive("bulletList") ? "is-active" : ""}`}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            title="Bullet List"
          >
            <List size={16} />
          </button>
          <button
            type="button"
            className={`tiptap-toolbar-btn ${editor.isActive("orderedList") ? "is-active" : ""}`}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            title="Ordered List"
          >
            <ListOrdered size={16} />
          </button>

          <div className="tiptap-toolbar-divider" />

          <button
            type="button"
            className={`tiptap-toolbar-btn ${isTextAlignActive("left") ? "is-active" : ""}`}
            onClick={() => handleTextAlign("left")}
            title="Align Left"
          >
            <AlignLeft size={16} />
          </button>
          <button
            type="button"
            className={`tiptap-toolbar-btn ${isTextAlignActive("center") ? "is-active" : ""}`}
            onClick={() => handleTextAlign("center")}
            title="Align Center"
          >
            <AlignCenter size={16} />
          </button>
          <button
            type="button"
            className={`tiptap-toolbar-btn ${isTextAlignActive("right") ? "is-active" : ""}`}
            onClick={() => handleTextAlign("right")}
            title="Align Right"
          >
            <AlignRight size={16} />
          </button>
          <button
            type="button"
            className={`tiptap-toolbar-btn ${isTextAlignActive("justify") ? "is-active" : ""}`}
            onClick={() => handleTextAlign("justify")}
            title="Align Justify"
          >
            <AlignJustify size={16} />
          </button>

          <button
            type="button"
            className="tiptap-toolbar-btn"
            onClick={() => editor.chain().focus().outdent().run()}
            title="Decrease Indent"
          >
            <OutdentIcon size={16} />
          </button>
          <button
            type="button"
            className="tiptap-toolbar-btn"
            onClick={() => editor.chain().focus().indent().run()}
            title="Increase Indent"
          >
            <IndentIcon size={16} />
          </button>

          <div className="tiptap-toolbar-divider" />

          <button
            type="button"
            className={`tiptap-toolbar-btn ${editor.isActive("image") ? "is-active" : ""}`}
            onClick={handleOpenImageModal}
            title="Insert or Edit Image"
          >
            <ImageIcon size={16} />
          </button>

        </div>



        {/* Editor Area */}
        <EditorContent editor={editor} className="tiptap-editor-content" />
      </div>

      {/* Media Picker Modal */}
      <MediaPickerModal
        show={showMediaPicker}
        onClose={handleCloseMediaPicker}
        onSelect={handleMediaSelect}
      />

      {/* Image Size Modal */}
      <Modal show={showImageModal} onHide={handleCloseImageModal} centered dialogClassName="custom-image-modal">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title style={{ fontSize: "20px", fontWeight: "500", color: "#2c3e50" }}>
            {isEditingImage ? "Edit Image" : "Insert Image"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2 pb-0">
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: "14px", color: "#495057" }}>Source URL</Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="https://example.com/image.png"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                style={{ fontSize: "14px", borderRadius: "6px" }}
              />
              <button
                type="button"
                onClick={handleOpenBrowseFromImageModal}
                style={{
                  backgroundColor: "#0D0D12",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "6px 16px",
                  fontSize: "14px",
                  fontWeight: "500",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => (e.target.style.backgroundColor = "#2c2c38")}
                onMouseLeave={(e) => (e.target.style.backgroundColor = "#0D0D12")}
              >
                Browse...
              </button>
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: "14px", color: "#495057" }}>Alternative description</Form.Label>
            <Form.Control
              type="text"
              placeholder="Alt text"
              value={imageAlt}
              onChange={(e) => setImageAlt(e.target.value)}
              style={{ fontSize: "14px", borderRadius: "6px" }}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: "14px", color: "#495057", fontWeight: "500" }}>Position / Text Wrapping</Form.Label>
            <div className="d-flex gap-2 flex-wrap">
              {[
                { id: "left", label: "⬅️ Float Left (Wrap Text)", desc: "Image on left, text wraps on right" },
                { id: "center", label: "↕️ Center (Block)", desc: "Centered, text above/below" },
                { id: "right", label: "➡️ Float Right (Wrap Text)", desc: "Image on right, text wraps on left" },
                { id: "inline", label: "↔️ Inline", desc: "Inline with text flow" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setImageAlign(opt.id)}
                  style={{
                    flex: "1 1 45%",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: imageAlign === opt.id ? "2px solid #0d6efd" : "1px solid #dee2e6",
                    backgroundColor: imageAlign === opt.id ? "#e7f1ff" : "#fff",
                    color: imageAlign === opt.id ? "#0d6efd" : "#212529",
                    fontWeight: imageAlign === opt.id ? "600" : "400",
                    fontSize: "13px",
                    textAlign: "left",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                >
                  <div>{opt.label}</div>
                  <div style={{ fontSize: "11px", color: "#6c757d" }}>{opt.desc}</div>
                </button>
              ))}
            </div>
          </Form.Group>

          <div className="d-flex gap-3 mb-3">
            <Form.Group className="flex-fill">
              <Form.Label style={{ fontSize: "14px", color: "#495057" }}>Width</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. 100% or 300"
                value={imageWidth}
                onChange={(e) => setImageWidth(e.target.value)}
                style={{ fontSize: "14px", borderRadius: "6px" }}
              />
            </Form.Group>
            <Form.Group className="flex-fill">
              <Form.Label style={{ fontSize: "14px", color: "#495057" }}>Height</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. auto or 200"
                value={imageHeight}
                onChange={(e) => setImageHeight(e.target.value)}
                style={{ fontSize: "14px", borderRadius: "6px" }}
              />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 pb-4 pe-4 d-flex justify-content-end">
          <Button
            variant="light"
            onClick={handleCloseImageModal}
            style={{ backgroundColor: "#e9ecef", border: "none", color: "#495057", fontSize: "14px", padding: "6px 16px", borderRadius: "6px", marginRight: "8px" }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleInsertImage}
            style={{ backgroundColor: "#007bff", border: "none", fontSize: "14px", padding: "6px 16px", borderRadius: "6px" }}
          >
            {isEditingImage ? "Update Image" : "Insert"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Link Modal */}
      <Modal show={showLinkModal} onHide={() => setShowLinkModal(false)} centered dialogClassName="custom-link-modal">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title style={{ fontSize: "20px", fontWeight: "500", color: "#2c3e50" }}>
            {editor?.isActive("link") ? "Edit Link" : "Insert Link"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2 pb-0">
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: "14px", color: "#495057" }}>Link URL</Form.Label>
            <Form.Control
              type="text"
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              style={{ fontSize: "14px", borderRadius: "6px" }}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: "14px", color: "#495057" }}>Text to display</Form.Label>
            <Form.Control
              type="text"
              placeholder="Link text"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              style={{ fontSize: "14px", borderRadius: "6px" }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 pb-4 pe-4 d-flex justify-content-end">
          {editor?.isActive("link") && (
            <Button
              variant="outline-danger"
              onClick={() => {
                editor?.chain().focus().unsetLink().run();
                setShowLinkModal(false);
                setLinkUrl("");
                setLinkText("");
              }}
              style={{ fontSize: "14px", padding: "6px 16px", borderRadius: "6px", marginRight: "auto" }}
            >
              Remove Link
            </Button>
          )}
          <Button
            variant="light"
            onClick={() => setShowLinkModal(false)}
            style={{ backgroundColor: "#e9ecef", border: "none", color: "#495057", fontSize: "14px", padding: "6px 16px", borderRadius: "6px", marginRight: "8px" }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleInsertLink}
            style={{ backgroundColor: "#007bff", border: "none", fontSize: "14px", padding: "6px 16px", borderRadius: "6px" }}
          >
            {editor?.isActive("link") ? "Update Link" : "Insert Link"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Table Modal */}
      <Modal show={showTableModal} onHide={() => setShowTableModal(false)} centered dialogClassName="custom-table-modal">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title style={{ fontSize: "20px", fontWeight: "500", color: "#2c3e50" }}>Insert Table</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2 pb-0">
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: "14px", color: "#495057" }}>Rows</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={tableRows}
              onChange={(e) => setTableRows(parseInt(e.target.value) || 1)}
              style={{ fontSize: "14px", borderRadius: "6px" }}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: "14px", color: "#495057" }}>Columns</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={tableCols}
              onChange={(e) => setTableCols(parseInt(e.target.value) || 1)}
              style={{ fontSize: "14px", borderRadius: "6px" }}
            />
          </Form.Group>
          <Form.Group className="mb-2 d-flex align-items-center gap-2">
            <Form.Check
              type="checkbox"
              id="table-header-checkbox"
              checked={tableHeaders}
              onChange={(e) => setTableHeaders(e.target.checked)}
              style={{ cursor: "pointer" }}
            />
            <Form.Label htmlFor="table-header-checkbox" style={{ fontSize: "14px", color: "#495057", cursor: "pointer", margin: 0 }}>
              Include Header Row
            </Form.Label>
          </Form.Group>
          <Form.Group className="mb-3 d-flex align-items-center gap-2">
            <Form.Check
              type="checkbox"
              id="table-borderless-checkbox"
              checked={tableBorderless}
              onChange={(e) => setTableBorderless(e.target.checked)}
              style={{ cursor: "pointer" }}
            />
            <Form.Label htmlFor="table-borderless-checkbox" style={{ fontSize: "14px", color: "#495057", cursor: "pointer", margin: 0 }}>
              Borderless Grid Layout (Resizable Columns)
            </Form.Label>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 pb-4 pe-4 d-flex justify-content-end">
          <Button
            variant="light"
            onClick={() => setShowTableModal(false)}
            style={{ backgroundColor: "#e9ecef", border: "none", color: "#495057", fontSize: "14px", padding: "6px 16px", borderRadius: "6px", marginRight: "8px" }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleInsertTable}
            style={{ backgroundColor: "#007bff", border: "none", fontSize: "14px", padding: "6px 16px", borderRadius: "6px" }}
          >
            Insert
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Map Modal */}
      <Modal show={showMapModal} onHide={() => setShowMapModal(false)} centered dialogClassName="custom-map-modal">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title style={{ fontSize: "20px", fontWeight: "500", color: "#2c3e50" }}>Insert Map</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2 pb-0">
          <p style={{ fontSize: "14px", color: "#495057", marginBottom: "16px" }}>
            Upload a GeoLibre project file (.geolibre.json), or paste the share URL of an existing project.
          </p>
          <Form.Group className="mb-3">
            <Form.Control
              type="file"
              accept=".json,.geolibre"
              onChange={(e) => setMapFile(e.target.files?.[0] || null)}
              style={{ fontSize: "14px" }}
            />
          </Form.Group>
          <div className="text-center my-3 text-muted" style={{ fontSize: "12px" }}>— OR —</div>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: "14px", color: "#495057" }}>GeoLibre project share URL</Form.Label>
            <Form.Control
              type="text"
              value={mapUrl}
              onChange={(e) => setMapUrl(e.target.value)}
              style={{ fontSize: "14px", borderRadius: "6px" }}
            />
          </Form.Group>
          <div className="d-flex gap-3 mb-3">
            <Form.Group className="flex-fill">
              <Form.Label style={{ fontSize: "14px", color: "#495057" }}>Width</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. 100%"
                value={mapWidth}
                onChange={(e) => setMapWidth(e.target.value)}
                style={{ fontSize: "14px", borderRadius: "6px" }}
              />
            </Form.Group>
            <Form.Group className="flex-fill">
              <Form.Label style={{ fontSize: "14px", color: "#495057" }}>Height</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. 400"
                value={mapHeight}
                onChange={(e) => setMapHeight(e.target.value)}
                style={{ fontSize: "14px", borderRadius: "6px" }}
              />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 pb-4 pe-4 d-flex justify-content-end">
          <Button
            variant="light"
            onClick={() => setShowMapModal(false)}
            disabled={isUploadingMap}
            style={{ backgroundColor: "#e9ecef", border: "none", color: "#495057", fontSize: "14px", padding: "6px 16px", borderRadius: "6px", marginRight: "8px" }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleInsertMap}
            disabled={isUploadingMap}
            style={{ backgroundColor: "#007bff", border: "none", fontSize: "14px", padding: "6px 16px", borderRadius: "6px" }}
          >
            {isUploadingMap ? "Uploading..." : "Insert"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Right-Click Popup Context Menu (Google Docs Style) */}
      {contextMenu.visible && (
        <div
          className="tiptap-context-menu"
          style={{ top: `${contextMenu.y}px`, left: `${contextMenu.x}px` }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.isImage ? (
            <>
              <button
                type="button"
                className="context-menu-item"
                onClick={() => {
                  handleOpenImageModal();
                  setContextMenu({ visible: false });
                }}
              >
                <Settings size={14} /> Image options
              </button>
              <button
                type="button"
                className="context-menu-item"
                onClick={() => {
                  setShowMediaPicker(true);
                  setContextMenu({ visible: false });
                }}
              >
                <ImageIcon size={14} /> Replace image
              </button>
              <button
                type="button"
                className="context-menu-item"
                onClick={() => {
                  const src = editor?.getAttributes("image").src;
                  if (src) {
                    navigator.clipboard.writeText(src);
                    toast.success("Image URL copied to clipboard");
                  }
                  setContextMenu({ visible: false });
                }}
              >
                <Copy size={14} /> Copy image URL <span className="context-menu-shortcut">Ctrl+C</span>
              </button>
              <div className="context-menu-divider" />
              <button
                type="button"
                className="context-menu-item"
                onClick={() => {
                  handleOpenLinkModal();
                  setContextMenu({ visible: false });
                }}
              >
                <Link2 size={14} /> Insert link <span className="context-menu-shortcut">Ctrl+K</span>
              </button>
              <div className="context-menu-divider" />
              <button
                type="button"
                className="context-menu-item danger"
                onClick={() => {
                  editor?.chain().focus().deleteSelection().run();
                  setContextMenu({ visible: false });
                }}
              >
                <Trash2 size={14} /> Delete
              </button>
            </>
          ) : contextMenu.isTable ? (
            <>
              <button
                type="button"
                className="context-menu-item"
                onClick={() => {
                  editor?.chain().focus().addRowBefore().run();
                  setContextMenu({ visible: false });
                }}
              >
                + Insert row above
              </button>
              <button
                type="button"
                className="context-menu-item"
                onClick={() => {
                  editor?.chain().focus().addRowAfter().run();
                  setContextMenu({ visible: false });
                }}
              >
                + Insert row below
              </button>
              <button
                type="button"
                className="context-menu-item danger"
                onClick={() => {
                  editor?.chain().focus().deleteRow().run();
                  setContextMenu({ visible: false });
                }}
              >
                <Trash2 size={14} /> Delete row
              </button>
              <div className="context-menu-divider" />
              <button
                type="button"
                className="context-menu-item"
                onClick={() => {
                  editor?.chain().focus().addColumnBefore().run();
                  setContextMenu({ visible: false });
                }}
              >
                + Insert column left
              </button>
              <button
                type="button"
                className="context-menu-item"
                onClick={() => {
                  editor?.chain().focus().addColumnAfter().run();
                  setContextMenu({ visible: false });
                }}
              >
                + Insert column right
              </button>
              <button
                type="button"
                className="context-menu-item danger"
                onClick={() => {
                  editor?.chain().focus().deleteColumn().run();
                  setContextMenu({ visible: false });
                }}
              >
                <Trash2 size={14} /> Delete column
              </button>
              <div className="context-menu-divider" />
              <button
                type="button"
                className="context-menu-item danger"
                onClick={() => {
                  editor?.chain().focus().deleteTable().run();
                  setContextMenu({ visible: false });
                }}
              >
                <Trash2 size={14} /> Delete table
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="context-menu-item"
                onClick={() => {
                  handleOpenLinkModal();
                  setContextMenu({ visible: false });
                }}
              >
                <Link2 size={14} /> Insert link <span className="context-menu-shortcut">Ctrl+K</span>
              </button>
              <button
                type="button"
                className="context-menu-item"
                onClick={() => {
                  handleOpenImageModal();
                  setContextMenu({ visible: false });
                }}
              >
                <ImageIcon size={14} /> Insert image
              </button>
              <button
                type="button"
                className="context-menu-item"
                onClick={() => {
                  setShowTableModal(true);
                  setContextMenu({ visible: false });
                }}
              >
                <TableIcon size={14} /> Insert table
              </button>
              <div className="context-menu-divider" />
              <button
                type="button"
                className="context-menu-item"
                onClick={() => {
                  editor?.chain().focus().clearNodes().unsetAllMarks().run();
                  setContextMenu({ visible: false });
                }}
              >
                Clear formatting
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default TinyMceEditor;
